import CloseIcon from '@src/assets/images/common/icon/files/icon-close-files.svg';
import PhotoGrayIcon from '@src/assets/images/common/icon/photo-gray.svg';
import useHeicConverter from '@src/hooks/useHeicConverter';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { Attachment } from '@src/idb';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLongPress } from 'use-long-press';

export type PreviewImageItemProps = {
  attachment: Attachment;
  upload?: boolean;
  onDelete?: () => void;
  onItemClick?: () => void;
  onLongPress?: () => void;
  selected?: boolean;
};

const PreviewImageItem: React.FC<PreviewImageItemProps> = ({
  attachment,
  upload = false,
  onDelete = () => {},
  onItemClick,
  onLongPress,
}) => {
  const [isHover, setHover] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [src, setSrc] = useState<string | undefined>('');
  const modalRoot = document.body;
  const [isSending, setSending] = useState<boolean>(false);
  const isMobile = useIsMobile();
  const { isHeicFile } = useHeicConverter(undefined, attachment.name);

  useEffect(() => {
    const sending = attachment.blob instanceof File && !attachment.chatAttachment?.file_url;
    setSending(sending);

    let url: string;
    if (sending && isHeicFile) {
      url = PhotoGrayIcon;
    } else if (sending) {
      url = URL.createObjectURL(attachment.blob);
    } else {
      url = attachment.chatAttachment?.thumbnail_url ?? attachment.chatAttachment?.file_url ?? '';
    }

    setSrc(url);

    return () => {
      if (sending && !isHeicFile) {
        URL.revokeObjectURL(url);
      }
    };
  }, [attachment.blob, attachment.chatAttachment, isHeicFile]);

  const handleClick = () => {
    if (onItemClick) {
      onItemClick();
      return;
    }

    if (isMobile || isHover) {
      setModalOpen(true);
      return;
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const longPressBind = useLongPress(
    () => {
      if (isMobile && onLongPress) {
        onLongPress();
      }
    },
    { threshold: 650, cancelOnMovement: 10 },
  );

  return (
    <>
      <div
        className='relative size-full cursor-pointer overflow-hidden rounded-md'
        onMouseEnter={() => !isMobile && setHover(true)}
        onMouseLeave={() => !isMobile && setHover(false)}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        {...(isMobile && onLongPress ? longPressBind() : {})}
        style={{
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: 'manipulation',
        }}
      >
        {src !== '' && (
          <img
            src={src}
            alt={attachment.name}
            className='size-full object-cover'
            draggable={false}
            onDragStart={handleDragStart}
          />
        )}
        {upload && isHover && !isSending && !isMobile && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className='absolute right-[2px] top-[2px] z-20 size-5 place-items-center rounded-full'
            aria-label='削除'
          >
            <img
              src={CloseIcon}
              alt='削除'
              className='size-4'
            />
          </button>
        )}
        {isSending && (
          <div className='absolute right-1 top-1 size-4 animate-spin rounded-full border-2 border-[#5B8CEC] border-t-transparent'></div>
        )}
      </div>
      {isModalOpen &&
        createPortal(
          <div
            className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/75'
            onClick={() => setModalOpen(false)}
          >
            <div className='relative'>
              <img
                src={src}
                alt={attachment.name}
                className='max-h-full max-w-full'
              />
              <button
                type='button'
                onClick={() => setModalOpen(false)}
                className='absolute right-2 top-2 z-[10000] p-1'
                aria-label='閉じる'
              >
                <img
                  src={CloseIcon}
                  alt='閉じる'
                  className='size-6'
                />
              </button>
            </div>
          </div>,
          modalRoot,
        )}
    </>
  );
};

export default PreviewImageItem;
